import { AbilityBuilder } from '@casl/ability';

import { CacheProvider, EmotionCache } from '@emotion/react';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { ThemeProvider } from '@mui/material/styles';
import StylesProvider from '@mui/styles/StylesProvider';
import type { AppProps } from 'next/app';
import Feedback from 'packages/workspace/components/common/Feedback';
import { AbilityStore } from 'packages/workspace/services/store/abilities-store';
import { QueryClient, QueryClientProvider } from 'react-query';
import BackdropLoader from '../components/common/BackdropLoader';
import ConsentCheck from '../components/consent/ConsentCheck';
import createEmotionCache from '../design/createEmotionCache';
import '../design/styles/global.scss';
import theme from '../design/themes/theme';
import { AppAbilities } from '../modules/helpers/contexts/abilities';
import { AmplifyAuthProvider } from '../modules/helpers/contexts/auth';

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();
interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: process.env.NEXT_PUBLIC_APP_STAGE === 'production',
    },
  },
});

function CustomApp({ Component, pageProps, emotionCache = clientSideEmotionCache }: MyAppProps): JSX.Element {
  //Setting Permissions
  const { can: allow, rules } = new AbilityBuilder(AppAbilities);
  if (pageProps.permissions) {
    if (pageProps.permissions instanceof Array) {
      pageProps.permissions.forEach((model) => {
        if (model.access.create) allow('create', model.service);
        if (model.access.list) allow('list', model.service);
        if (model.access.update) allow('update', model.service);
        if (model.access.remove) allow('remove', model.service);
        if (model.access.get) allow('get', model.service);
      });
    } else {
      if (pageProps.permissions.create) allow('create', pageProps.namespace);
      if (pageProps.permissions.list) allow('list', pageProps.namespace);
      if (pageProps.permissions.update) allow('update', pageProps.namespace);
      if (pageProps.permissions.remove) allow('remove', pageProps.namespace);
      if (pageProps.permissions.get) allow('get', pageProps.namespace);
    }
    AbilityStore.updateRules(rules);
  }

  return (
    <CacheProvider value={emotionCache}>
      <QueryClientProvider client={queryClient}>
        <AmplifyAuthProvider>
          <StylesProvider injectFirst>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <div className="rhs-theme" role="application">
                    <BackdropLoader />
                    <Feedback />
                    <ConsentCheck>
                      <Component {...pageProps} />
                    </ConsentCheck>
                  </div>
                </LocalizationProvider>
              </ThemeProvider>
          </StylesProvider>
        </AmplifyAuthProvider>
      </QueryClientProvider>
    </CacheProvider>
  );
}

export default CustomApp;
